/**
 * Formatting utilities for Investment Tracker frontend.
 */

// ============================================
// Currency Formatting
// ============================================

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  COP: 'COP ',
  ARS: 'ARS ',
  MXN: 'MX$',
  BRL: 'R$',
};

/**
 * Format a number as a currency string.
 * @param {number} value
 * @param {string} currency
 * @param {number} decimals
 * @returns {string}
 */
export function formatCurrency(value, currency = 'USD', decimals = 2) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return `${CURRENCY_SYMBOLS[currency] || '$'}0.00`;

  const symbol = CURRENCY_SYMBOLS[currency] || `${currency} `;
  return `${symbol}${num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * Format a number as a compact currency (e.g., $1.2K, $3.5M).
 * @param {number} value
 * @param {string} currency
 * @returns {string}
 */
export function formatCurrencyCompact(value, currency = 'USD') {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const symbol = CURRENCY_SYMBOLS[currency] || '$';

  if (Math.abs(num) >= 1_000_000) {
    return `${symbol}${(num / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(num) >= 1_000) {
    return `${symbol}${(num / 1_000).toFixed(1)}K`;
  }
  return formatCurrency(value, currency);
}

// ============================================
// Percentage Formatting
// ============================================

/**
 * Format a number as a percentage string.
 * @param {number} value
 * @param {number} decimals
 * @returns {string}
 */
export function formatPercent(value, decimals = 2) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0.00%';
  return `${num.toFixed(decimals)}%`;
}

/**
 * Format a percentage with explicit + / - sign.
 * @param {number} value
 * @param {number} decimals
 * @returns {string}
 */
export function formatPercentSigned(value, decimals = 2) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const sign = num >= 0 ? '+' : '';
  return `${sign}${num.toFixed(decimals)}%`;
}

// ============================================
// Date Formatting
// ============================================

/**
 * Format an ISO date string or Date object into a human-readable date.
 * @param {string|Date} date
 * @param {boolean} dateOnly - if true, omit time component
 * @returns {string}
 */
export function formatDate(date, dateOnly = false) {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';

  const options = dateOnly
    ? { year: 'numeric', month: 'short', day: 'numeric' }
    : { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };

  return d.toLocaleDateString('en-US', options);
}

/**
 * Format a date as a relative string (e.g., "3 days ago", "in 5 days").
 * @param {string|Date} date
 * @returns {string}
 */
export function formatRelativeDate(date) {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = d - now;
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 30) return `In ${diffDays} days`;
  if (diffDays < -1 && diffDays > -30) return `${Math.abs(diffDays)} days ago`;

  return formatDate(date, true);
}

// ============================================
// Number Formatting
// ============================================

/**
 * Format a decimal number with up to n decimal places.
 * @param {number|string} value
 * @param {number} decimals
 * @returns {string}
 */
export function formatNumber(value, decimals = 2) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a quantity (share count) — remove trailing zeros for whole numbers.
 * @param {number|string} qty
 * @returns {string}
 */
export function formatQuantity(qty) {
  const num = parseFloat(qty);
  if (isNaN(num)) return '0';
  // Use up to 8 decimal places but strip trailing zeros
  return parseFloat(num.toFixed(8)).toString();
}

// ============================================
// Gain/Loss Helpers
// ============================================

/**
 * Get the CSS class for positive (green) or negative (red) values.
 * @param {number} value
 * @returns {string}
 */
export function gainLossClass(value) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num > 0) return 'text-green-600';
  if (num < 0) return 'text-red-600';
  return 'text-gray-600';
}

/**
 * Get a sign prefix string for gain/loss values.
 * @param {number} value
 * @returns {string}
 */
export function gainLossSign(value) {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return num >= 0 ? '+' : '';
}
